import { memo } from 'react';

import { cn } from '../../lib/styles';

export interface NavRailItemProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'children'
  > {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  /** Renders the item as this element instead of a button, e.g. a router link. */
  element?: React.ElementType;
}

/** Icon-over-label item for a vertical app navigation rail. */
const NavRailItemBase = ({
  icon,
  label,
  active = false,
  element: Comp = 'button',
  className,
  ...rest
}: NavRailItemProps) => {
  return (
    <Comp
      className={cn(
        'flex flex-col items-center gap-1 group rounded-sm cursor-pointer no-underline',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-6',
        className,
      )}
      {...(Comp === 'button' ? { type: 'button' } : {})}
      {...(active ? { 'aria-current': 'page', 'data-active': true } : {})}
      {...rest}
    >
      <span
        className={cn(
          'flex items-center justify-center p-1.5',
          'rounded-sm group-hover:bg-bg-inter-secondary-hover transition-all',
          active && 'bg-bg-sur-primary shadow-xsm group-hover:bg-gray-4/10',
        )}
      >
        {icon}
      </span>

      <span className="font-body-xs text-text-primary font-semibold truncate">{label}</span>
    </Comp>
  );
};

export const NavRailItem = memo(NavRailItemBase);
